import { useState, useEffect, useRef, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { API, Auth } from 'aws-amplify';

// Context and utilities
import { useAppContext } from '../../Lib/contextLib';
import { getVideo, GetUserVideo } from '../../s3Func/s3Download';

// Components
import PopUp from '../../Popup/logoutPopup';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import Learn from '../../Learn/Learn';
import Practice from './Practice';
import SingingTime from '../../SingingTime/SingingTime';
import Celebrate from '../../Celebrate/Celebrate';
import Surprise from '../../SurpriseVideo/surprise';
import FollowTheBeatModule10 from '../../FollowTheBeat/FollowTheBeatModule10.js';

// Assets
import presentSound from '../../Unit1/Module1/assets/ta_da_gift.mp3';

// Styles
import './Module15World.css';

// ============================================================================
// Constants
// ============================================================================

const AFTER_CELEBRATE_VIDEO_DAY9 = 'appfiles/active_listening/closing.mp4';
const MODULE_NAME = 'MODULE15';
const NAVIGATION_PATH = '/activelistening/navigation';
const SPECIAL_DAYS = { DAY_4: 4, DAY_8: 8 };
const COIN_PRIZE_THRESHOLD = 100;

// Step completion states
const STEPS = {
  NOT_STARTED: 'notstarted',
  LEARN: 'learn',
  PRACTICE: 'practice',
  FOLLOW_THE_BEAT: 'followthebeat',
  CELEBRATE: 'celebrate',
  SINGING_TIME: 'singingtime',
  SURPRISE: 'surprise',
};

// Grade types that bypass certain restrictions
const UNRESTRICTED_GRADES = ['Teacher Mode', 'Free Play'];

// ============================================================================
// Utility Functions
// ============================================================================

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
  const iosDevices = [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ];
  return (
    iosDevices.includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const isUnrestrictedGrade = (grade) => UNRESTRICTED_GRADES.includes(grade);

// ============================================================================
// Custom Hooks
// ============================================================================

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

// ============================================================================
// Main Component
// ============================================================================

const Module15World = () => {
  const { width: clientWidth, height: clientHeight } = useWindowDimensions();
  const { userHasAuthenticated } = useAppContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { module15 } = useSelector((state) => state.module15, shallowEqual);
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);

  // Refs for tracking initial renders
  const firstUpdate = useRef(true);
  const coinUpdate = useRef(true);

  // ------------------------------------------------------------------------
  // Image URL Generator
  // ------------------------------------------------------------------------
  const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

  // ------------------------------------------------------------------------
  // Image Assets
  // ------------------------------------------------------------------------
  const images = {
    // Backgrounds
    background: generateImageUrl('images/Module15Images/m15-BG-chromebook.png'),
    sky: generateImageUrl('images/Module15Images/U3m5_Sky.png'),
    mountain: generateImageUrl('images/Module15Images/U3m5_Mountains.png'),
    tree: generateImageUrl('images/Module15Images/Tree.png'),

    // Character
    bird: generateImageUrl('images/Module5Images/Bird+Closing+still.gif'),

    // Egg elements
    egg: generateImageUrl('images/Module5Images/Egg.png'),
    eggBottom: generateImageUrl('images/Module5Images/Egg+Crack+Bottom.png'),
    eggTop: generateImageUrl('images/Module5Images/Egg+Crack+Top.png'),
    eggHalfCracked: generateImageUrl('images/Module5Images/EggCrack_3.png'),
    nest: generateImageUrl('images/Module20Images/Nest.svg'),

    // UI Elements
    coinPick: generateImageUrl('images/Images/AwesomeCoin.png'),
    coinBar: generateImageUrl('images/Images/Coin+Counter.png'),
    journeyMap: generateImageUrl('images/Module1Images/bluejourneymap.svg'),
    greenCheck: generateImageUrl('images/Module1Images/GreenCheck-Button.png'),
    present: generateImageUrl('images/Images/Gift.png'),
    keyReward: generateImageUrl('images/Module5Images/Worm.png'),

    // Buttons
    logoutBtn: generateImageUrl('images/Module1Images/Logout.png'),
    reviewBtn: generateImageUrl('images/Module1Images/Button_Review.png'),
    rewardBtn: generateImageUrl('images/Module1Images/Button_Reward_3x.png'),
    celebrateBtn: generateImageUrl('images/Module1Images/Button_Celebrate.png'),
    learnBtn: generateImageUrl('images/Module1Images/Button_Learn.png'),
    practiceBtn: generateImageUrl('images/Module1Images/Button_Practice.png'),
    ftbBtn: generateImageUrl('images/Images/Button_FTB.png'),
    singingTimeBtn: generateImageUrl('images/Module1Images/Button_SingingTime.png'),

    // Progress bars
    progressBars: [
      generateImageUrl('images/Module5Images/Mod5_ProgressBar_0.svg'),
      generateImageUrl('images/Module5Images/Mod5_ProgressBar_Third.svg'),
      generateImageUrl('images/Module5Images/Mod5_ProgressBar_2Thirds.svg'),
      generateImageUrl('images/Module5Images/Mod5_ProgressBar_2Thirds.svg'),
      generateImageUrl('images/Module5Images/Mod5_ProgressBar_Completed.svg'),
    ],

    // Rewards
    rewards: {
      TreeHouseRed: generateImageUrl('images/Module5Images/Reward_BirdhousePink.png'),
      TreeHouseBlue: generateImageUrl('images/Module5Images/Reward_BirdhouseBlue.png'),
      Alien1: generateImageUrl('images/Module10Images/DailyReward_Alien1.png'),
      Alien2: generateImageUrl('images/Module10Images/DailyReward_Alien2.png'),
      TelescopeRed: generateImageUrl('images/Module5Images/Reward_TelescopeRed.png'),
      TelescopeBlue: generateImageUrl('images/Module5Images/Reward_TelescopeBlue.png'),
      CometOrange: generateImageUrl('images/Module10Images/DailyReward_CometOrange.png'),
      CometBlue: generateImageUrl('images/Module10Images/DailyReward_CometBlue.png'),
      Deer: generateImageUrl('images/Module5Images/Reward_DeerMale.png'),
      Doe: generateImageUrl('images/Module5Images/Reward_DeerFemale.png'),
      Planet1: generateImageUrl('images/Module10Images/DailyReward_Planet1.png'),
      Planet2: generateImageUrl('images/Module10Images/DailyReward_Planet2.png'),
      Swing: generateImageUrl('images/Module5Images/Reward_WoodenSwing.png'),
      TireSwing: generateImageUrl('images/Module5Images/Reward_TireSwing.png'),
      Rocket1: generateImageUrl('images/Module10Images/DailyReward_Rocket1.png'),
      Rocket2: generateImageUrl('images/Module10Images/DailyReward_Rocket2.png'),
      NorthernLights: generateImageUrl('images/Module15Images/BigReward_NorthLights.png'),
      Galaxy: generateImageUrl('images/Module10Images/BigReward_Galaxy.png'),
    },
  };

  // Progress bar arrays for different day types
  const progressBar5Keys = [
    images.progressBars[0],
    images.progressBars[1],
    images.progressBars[2],
    images.progressBars[2],
    images.progressBars[3],
    images.progressBars[4],
  ];
  const progressBar4Keys = images.progressBars;

  // Reward options by day
  const rewardOptionsByDay = {
    1: [images.rewards.TreeHouseRed, images.rewards.TreeHouseBlue],
    2: [images.rewards.Alien1, images.rewards.Alien2],
    3: [images.rewards.TelescopeRed, images.rewards.TelescopeBlue],
    4: [images.rewards.CometBlue, images.rewards.CometOrange],
    5: [images.rewards.Deer, images.rewards.Doe],
    6: [images.rewards.Planet1, images.rewards.Planet2],
    7: [images.rewards.Swing, images.rewards.TireSwing],
    8: [images.rewards.Rocket1, images.rewards.Rocket2],
  };

  // Influencers array
  const influencers = [
    login.influencerid,
    login.influencerid1,
    login.influencerid2,
    login.influencerid3,
    login.influencerid4,
  ];

  // Audio elements
  const touchSound = new Audio(generateImageUrl('media/Module1Videos/touch_button.mp3'));
  const eggCrackAudio = new Audio(generateImageUrl('media/Module5Videos/egg_crack.mp3'));

  // ------------------------------------------------------------------------
  // UI State
  // ------------------------------------------------------------------------
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Icon visibility state
  const [reviewIconVisible, setReviewIconVisible] = useState(false);
  const [keyIcon, setKeyIcon] = useState(false);
  const [coinIcon, setCoinIcon] = useState(false);
  const [presentIcon, setPresentIcon] = useState(false);

  // Reward selection state
  const [rewardOption1, setRewardOption1] = useState(null);
  const [rewardOption2, setRewardOption2] = useState(null);
  const [displayReward, setDisplayReward] = useState(false);
  const [displayGreenCheck, setDisplayGreenCheck] = useState(false);
  const [dropZone, setDropZone] = useState(false);
  const [displayGreenButton, setDisplayGreenButton] = useState(false);

  // Egg animation state
  const [eggAnimate, setEggAnimate] = useState(false);
  const [eggAnimationState, setEggAnimationState] = useState('');
  const [keyState, setKeyState] = useState('');
  const [eggHalf, setEggHalf] = useState(false);
  const [eggState, setEggState] = useState(images.egg);

  // Screen/component visibility state
  const [displayLearn, setDisplayLearn] = useState(false);
  const [displayPractice, setDisplayPractice] = useState(false);
  const [displayDay4Practice, setDisplayDay4Practice] = useState(false);
  const [displaySingingTime, setDisplaySingingTime] = useState(false);
  const [displayFTB, setDisplayFTB] = useState(false);
  const [displayCelebrate, setDisplayCelebrate] = useState(false);
  const [displayBody, setDisplayBody] = useState(true);
  const [displayIntroVideo, setDisplayIntroVideo] = useState(false);
  const [displaySurprise, setDisplaySurprise] = useState(false);
  const [displayVoice, setDisplayVoice] = useState(false);

  // Component content state
  const [learnComponent, setLearnComponent] = useState('');
  const [practiceComponent, setPracticeComponent] = useState('');
  const [singTimeComponent, setSingTimeComponent] = useState('');
  const [celebrateComponent, setCelebrateComponent] = useState('');
  const [introVideo, setIntroVideo] = useState('');
  const [surpriseVideo, setSurpriseVideo] = useState('');

  // Animal/Bird state
  const [showAnimal, setShowAnimal] = useState(false);
  const [animalState, setAnimalState] = useState('');
  const [animalVoice, setAnimalVoice] = useState('');
  const [playAnimalClose, setPlayAnimalClose] = useState(false);

  // Prize state
  const [prize, setPrize] = useState(false);
  const [displayPrize, setDisplayPrize] = useState(false);

  // Animation state
  const [coinAnimationState, setCoinAnimationState] = useState('bounceIcons');

  // Audio state
  const [touchButton, setTouchButton] = useState(false);
  const [checkSound, setCheckSound] = useState(false);
  const [buttonSound, setButtonSound] = useState(false);
  const [rewardsFound, setRewardsFound] = useState(false);
  const [coinAudio, setCoinAudio] = useState(false);

  // Tracking state
  const [checkStep, setCheckStep] = useState('');
  const [showReward, setShowReward] = useState(false);

  // Follow The Beat (FTB) assets
  const [ftbMusic, setFTBMusic] = useState('');
  const [ftbInstruction, setFTBInstruction] = useState('');
  const [ftbInstructionGIF, setFTBInstructionGIF] = useState('');
  const [ftbCount, setFTBCount] = useState('');
  const [ftbTutorial1, setFTBTutorial1] = useState('');
  const [ftbTutorial2, setFTBTutorial2] = useState('');
  const [ftbWalkthrough, setFTBWalkthrough] = useState('');

  // GIF and audio assets
  const [specialMessage, setSpecialMessage] = useState('');
  const [watchConversation, setWatchConversation] = useState('');
  const [amazingJob, setAmazingJob] = useState('');
  const [birdAwesomeJobGIF, setBirdAwesomeJobGIF] = useState('');
  const [practiceIntro, setPracticeIntro] = useState('');
  const [learnIntro, setLearnIntro] = useState('');
  const [ftbIntro, setFtbIntro] = useState('');
  const [celebrateIntro, setCelebrateIntro] = useState('');
  const [animalClose, setAnimalClose] = useState('');
  const [animalCloseAudio, setAnimalCloseAudio] = useState('');

  // ------------------------------------------------------------------------
  // Audio Helper
  // ------------------------------------------------------------------------

  const playButtonAudio = useCallback(() => {
    sleep(700).then(() => {
      setButtonSound(true);
    });
  }, []);

  // ------------------------------------------------------------------------
  // Asset Loading
  // ------------------------------------------------------------------------

  const loadGifAssets = useCallback(() => {
    // Common GIF assets
    setSpecialMessage(getVideo('appfiles/close/practice special message.gif', login));
    setWatchConversation(getVideo('appfiles/close/practice watch conversation.gif', login));
    setAmazingJob(getVideo('appfiles/close/close_awesome_job.mp3', login));
    setBirdAwesomeJobGIF(getVideo('appfiles/close/awesome job.gif', login));
    setPracticeIntro(getVideo('appfiles/close/practice intro.gif', login));
    setLearnIntro(getVideo('appfiles/caring/caring_learn_intro.gif', login));
    setFtbIntro(getVideo('appfiles/close/ftb intro.gif', login));
    setCelebrateIntro(getVideo('appfiles/close/close_celebrate_intro.gif', login));
    setAnimalClose(getVideo('appfiles/close/day finished.gif', login));
    setFTBInstructionGIF(getVideo('appfiles/close/FTB intructions.gif', login));
    setFTBCount(getVideo('appfiles/close/countingBird.gif', login));

    // Audio assets
    setAnimalCloseAudio(getVideo('appfiles/active_listening/active_listening_day_finished.mp3', login));
    setFTBInstruction(getVideo('appfiles/active_listening/active_listening_ftb_instructions.mp3', login));
    setFTBMusic(getVideo('appfiles/close/close_ftb_audio.m4a', login));

    // Tutorial assets (only for first-time users)
    if (!module15.unlockedDays.day2) {
      setFTBTutorial1(getVideo('appfiles/greet/ftb_walkthrough_touch_circle.mp3', login));
      setFTBTutorial2(getVideo('appfiles/active_listening/ftb_walkthrough_tap.mp3', login));
      setFTBWalkthrough(getVideo('appfiles/close/ftb_walkthrough_audio.mp3', login));
    }
  }, [login, module15.unlockedDays.day2]);

  useEffect(() => {
    loadGifAssets();
  }, [loadGifAssets]);

  // ------------------------------------------------------------------------
  // Game State Management
  // ------------------------------------------------------------------------

  const createGameDetails = useCallback(
    (flag, reward, coin) => {
      const gameDetail = { ...module15.game_details };

      if (flag) {
        const flagEntry = { flag, lastUpdated: new Date().toJSON() };
        gameDetail.moduleFlags.push(flagEntry);
      }

      if (coin >= 0) {
        gameDetail.tokens_earned = coin;
      }

      if (reward) {
        const dailyRewards = Object.entries(module15.rewards)
          .filter(([, rewardData]) => rewardData.unlocked)
          .map(([key, rewardData]) => ({
            type: key,
            x_multiplier: rewardData.x,
            y_multiplier: rewardData.y,
          }));
        gameDetail.dailyRewards = dailyRewards;
      }

      dispatch({ type: 'MODULE15_SET_GameDetials', payload: gameDetail });
    },
    [dispatch, module15.game_details, module15.rewards]
  );

  // ------------------------------------------------------------------------
  // API Calls
  // ------------------------------------------------------------------------

  const updateProgress = async () => {
    const payload = {
      body: {
        id: login.studentid,
        module: 'ActiveListening',
        day_completed: module15.currentday,
        time_completed: new Date().toDateString(),
      },
    };

    try {
      await API.put('TIR', '/v3/student/progress', payload);
    } catch (error) {
      // Silent fail - progress update is non-critical
    }
  };

  const updateDB = async () => {
    const modulePayload = {
      body: {
        id: module15.moduleID,
        day_completed: module15.day_completed,
        step_completed: module15.stepCompleted,
        game_details: JSON.stringify(module15.game_details),
      },
    };

    try {
      await API.put('TIR', '/v3/student/module', modulePayload);

      if (displayGreenButton) {
        setShowAnimal(true);
        setPlayAnimalClose(true);
        setAnimalState(animalClose);
        setDisplayGreenButton(false);
        setDisplayReward(false);
      }
    } catch (error) {
      const criticalSteps = [STEPS.NOT_STARTED, STEPS.LEARN, STEPS.PRACTICE, STEPS.CELEBRATE, STEPS.FOLLOW_THE_BEAT];
      if (criticalSteps.includes(module15.stepCompleted)) {
        window.alert('Connection is lost. Please login again.');
        dispatch({ type: 'CLEAR' });
        Auth.signOut();
        navigate('/');
      }
    }

    // Update current module for student
    const studentPayload = {
      body: {
        id: login.studentid,
        current_module: 'ActiveListening',
      },
    };

    try {
      await API.put('TIR', '/v3/student', studentPayload);
    } catch (error) {
      // Silent fail - student update is non-critical
    }
  };

  // ------------------------------------------------------------------------
  // Effects for State Synchronization
  // ------------------------------------------------------------------------

  // Sync step completion to database
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (!isUnrestrictedGrade(login.grade)) {
      updateDB();
    }
  }, [module15.stepCompleted]);

  // Handle section completion
  useEffect(() => {
    const shouldUpdateProgress =
      (module15.reviewIcon || login.grade === 'Teacher Mode') &&
      module15.sectionCompleted === 4;

    if (shouldUpdateProgress) {
      updateProgress();
      module15.sectionCompleted = 0;
    }
  }, [module15.sectionCompleted]);

  // Handle coin updates and prize eligibility
  useEffect(() => {
    if (module15.coins >= COIN_PRIZE_THRESHOLD) {
      setPrize(true);
    }

    if (coinUpdate.current) {
      coinUpdate.current = false;
      return;
    }

    createGameDetails(false, false, module15.coins);
    updateDB();
  }, [module15.coins]);

  // ------------------------------------------------------------------------
  // UI Handlers
  // ------------------------------------------------------------------------

  const togglePopup = () => {
    setIsPopupOpen((prev) => !prev);
  };

  // Handle progress key updates based on step completion
  useEffect(() => {
    const isDayCompleted = module15.currentday <= module15.day_completed;
    const hasUnrestrictedAccess = isUnrestrictedGrade(login.grade);

    if (isDayCompleted || hasUnrestrictedAccess) {
      // Day is completed or user has unrestricted access
      if (hasUnrestrictedAccess) {
        setShowReward(true);
      }

      dispatch({ type: 'MODULE15_TOGGLE_REVIEW_ICON', payload: true });

      const isSpecialDay = module15.currentday === SPECIAL_DAYS.DAY_4 || module15.currentday === SPECIAL_DAYS.DAY_8;
      dispatch({ type: 'MODULE15_SET_KEY', payload: isSpecialDay ? 5 : 4 });
    } else {
      // Day is in progress
      if (module15.stepCompleted === checkStep) {
        setCoinIcon(true);
      }

      dispatch({ type: 'MODULE15_TOGGLE_REVIEW_ICON', payload: false });

      const isDay4Start = module15.currentday === SPECIAL_DAYS.DAY_4 && module15.day_completed === 3;

      if (isDay4Start) {
        // Day 4 has a different step sequence (includes singing time first)
        const day4KeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.SINGING_TIME]: 1,
          [STEPS.LEARN]: 2,
          [STEPS.PRACTICE]: 3,
          [STEPS.FOLLOW_THE_BEAT]: 4,
          [STEPS.CELEBRATE]: 5,
        };

        const keyValue = day4KeyMap[module15.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE15_SET_KEY', payload: keyValue });
          if (module15.stepCompleted === STEPS.CELEBRATE) {
            setKeyIcon(true);
          }
        }
      } else {
        // Standard day progression
        const standardKeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.LEARN]: 1,
          [STEPS.PRACTICE]: 2,
          [STEPS.FOLLOW_THE_BEAT]: 3,
          [STEPS.CELEBRATE]: 4,
          [STEPS.SURPRISE]: 5,
        };

        const keyValue = standardKeyMap[module15.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE15_SET_KEY', payload: keyValue });

          // Handle special cases for celebrate step
          if (module15.stepCompleted === STEPS.CELEBRATE) {
            const isDay8Surprise = module15.currentday === SPECIAL_DAYS.DAY_8 && module15.day_completed === 7;
            if (isDay8Surprise) {
              setPresentIcon(true);
            } else {
              setKeyIcon(true);
            }
          } else if (module15.stepCompleted === STEPS.SURPRISE) {
            setKeyIcon(true);
          }
        }
      }

      playButtonAudio();
    }
  }, [module15.stepCompleted]);

  // ------------------------------------------------------------------------
  // Reward Selection Handlers
  // ------------------------------------------------------------------------

  const handleRewardSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;
    const day = module15.currentday;

    // Update Redux state
    dispatch({ type: `MODULE15_SET_REWARD${day}1`, payload: isOption1 });
    dispatch({ type: `MODULE15_SET_REWARD${day}2`, payload: !isOption1 });

    // Update visual selection feedback
    document.getElementById('rewardItemLeft2').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight2').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handleRewardConfirmation = () => {
    setCheckSound(true);
    setDropZone(true);
    setDisplayGreenCheck(false);
    setDisplayGreenButton(true);
  };

  const handleDayCompletion = () => {
    setCheckSound(true);
    setDropZone(false);
    updateProgress();

    sleep(500).then(() => {
      createGameDetails(false, true);

      if (isUnrestrictedGrade(login.grade)) {
        updateDB();
      }

      const nextDay = module15.currentday + 1;
      const completedDay = module15.currentday === SPECIAL_DAYS.DAY_8 ? 9 : module15.currentday;

      dispatch({ type: `MODULE15_SET_DAY_UNLOCK${nextDay}`, payload: true });
      dispatch({ type: 'MODULE15_SET_dayCompleted', payload: completedDay });
      dispatch({ type: 'MODULE15_SET_stepCompleted', payload: STEPS.NOT_STARTED });
    });
  };

  const renderRewardSelection = () => (
    <>
      <div className="rewards" id="rewardList">
        <div className="circle" id="rewardItemLeft2">
          <img
            src={rewardOption1}
            alt="Reward option 1"
            onClick={() => handleRewardSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight2">
          <img
            src={rewardOption2}
            alt="Reward option 2"
            onClick={() => handleRewardSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={handleRewardConfirmation}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">
            <div id="word">Drag and drop your item</div>
          </div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Complete day"
            onClick={handleDayCompletion}
          />
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------------
  // Prize Selection Handlers
  // ------------------------------------------------------------------------

  const handlePrizeSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;

    dispatch({ type: 'MODULE15_SET_PRIZE1', payload: isOption1 });
    dispatch({ type: 'MODULE15_SET_PRIZE2', payload: !isOption1 });

    document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handlePrizeConfirmation = () => {
    setDisplayPrize(false);
    setCheckSound(true);
    setDropZone(false);

    sleep(500).then(() => {
      setDisplayGreenButton(false);
      createGameDetails(false, true);
      dispatch({ type: 'MODULE15_SET_COIN', payload: module15.coins - COIN_PRIZE_THRESHOLD });
    });
  };

  const renderPrizeSelection = () => (
    <>
      {!dropZone && (
        <div className="rewards">
          <div className="circle" id="rewardItemLeft">
            <img
              src={images.rewards.NorthernLights}
              alt="Northern Lights prize"
              onClick={() => handlePrizeSelection(1)}
            />
          </div>
          <div className="circle" id="rewardItemRight">
            <img
              src={images.rewards.Galaxy}
              alt="Galaxy prize"
              onClick={() => handlePrizeSelection(2)}
            />
          </div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={() => {
              setCheckSound(true);
              setDropZone(true);
              setDisplayGreenCheck(false);
              setDisplayGreenButton(true);
            }}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">
            <div id="word">Drag and drop your item</div>
          </div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm prize"
            onClick={handlePrizeConfirmation}
          />
        )}
      </div>
    </>
  );

  // Set reward options based on current day
  useEffect(() => {
    const options = rewardOptionsByDay[module15.currentday];

    if (options) {
      setRewardOption1(options[0]);
      setRewardOption2(options[1]);
    } else {
      setDisplayGreenButton(true);
    }
  }, [keyIcon, module15.currentday]);

  // ------------------------------------------------------------------------
  // Review Mode Rendering
  // ------------------------------------------------------------------------

  const renderReviewButtons = () => {
    const isDay4 = module15.currentday === SPECIAL_DAYS.DAY_4;

    if (isDay4) {
      // Day 4 includes singing time button
      return (
        <div className="world2reviewContainer">
          <img
            className="reviewBtns41"
            src={images.singingTimeBtn}
            alt="singing button"
            onClick={displaySinging}
          />
          <img
            className="reviewBtns42"
            src={images.learnBtn}
            alt="learn button"
            onClick={openLearn}
          />
          <img
            className="reviewBtns43"
            src={images.practiceBtn}
            alt="practice button"
            onClick={openPractice}
          />
          <img
            className="reviewBtns44"
            src={images.ftbBtn}
            alt="ftb button"
            onClick={openFTB}
          />
          <img
            className="reviewBtns45"
            src={images.celebrateBtn}
            alt="celebrate button"
            onClick={openCelebrate}
          />
        </div>
      );
    }

    // Standard review buttons
    return (
      <div className="world2reviewContainer">
        <img
          className="reviewBtns1"
          src={images.learnBtn}
          alt="learn button"
          onClick={openLearn}
        />
        <img
          className="reviewBtns2"
          src={images.practiceBtn}
          alt="practice button"
          onClick={openPractice}
        />
        <img
          className="reviewBtns3"
          src={images.ftbBtn}
          alt="ftb button"
          onClick={openFTB}
        />
        <img
          className="reviewBtns4"
          src={images.celebrateBtn}
          alt="celebrate button"
          onClick={openCelebrate}
        />
      </div>
    );
  };

  // ------------------------------------------------------------------------
  // Video and Activity Handlers
  // ------------------------------------------------------------------------

  const handleIntroVideoEnd = () => {
    setDisplayIntroVideo(false);
    setDisplayBody(true);

    if (module15.currentday === SPECIAL_DAYS.DAY_4) {
      dispatch({ type: 'MODULE15_SET_KEY', payload: 1 });
      playButtonAudio();
    }

    if (module15.day_completed === 9) {
      navigate(NAVIGATION_PATH);
    }
  };

  const renderIntroduction = () => (
    <div>
      <CloudfrontVideoFormat
        source={introVideo}
        back={NAVIGATION_PATH}
        background={images.background}
        token={login.cookie}
        ended={handleIntroVideoEnd}
      />
    </div>
  );

  // Learn video configuration by day
  const learnVideosByDay = {
    1: { video: 'appfiles/active_listening/learn_day_1.mp4', closingVideo: 'appfiles/active_listening/learn_day_1_closing.mp4' },
    2: { video: 'appfiles/active_listening/learn_day_2.mp4', closingVideo: 'appfiles/active_listening/learn_day_2_closing.mp4' },
    3: { video: 'appfiles/active_listening/learn_day_3.mp4', closingVideo: 'appfiles/active_listening/learn_day_3_closing.mp4' },
  };

  const openLearn = async () => {
    const song = getVideo('appfiles/active_listening/active_listening_demo.m4a', login);
    const video = getVideo('appfiles/active_listening/learn_video.mp4', login);
    const dayConfig = learnVideosByDay[module15.currentday];

    if (dayConfig) {
      // Days 1-3: Use pre-configured videos
      setLearnComponent(
        <Learn
          video={getVideo(dayConfig.video, login)}
          songVideo={video}
          closingVideo={getVideo(dayConfig.closingVideo, login)}
          destiny="/activelistening"
          song={song}
          back="/activelistening"
          background={images.background}
          setDisplayLearn={setDisplayLearn}
          step_completed="MODULE15_SET_stepCompleted"
          module={module15}
        />
      );
    } else {
      // Days 4+: Load user-generated content
      setAnimalVoice(getVideo('appfiles/active_listening/active_listening_learn_intro.mp3', login));
      setDisplayVoice(true);
      setAnimalState(learnIntro);
      setShowAnimal(true);

      try {
        const userSongUrl = await GetUserVideo(`userfiles/${module15.StudentID}/active_listening_play_day_music.m4a`);
        const response = await fetch(userSongUrl);
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module15.currentday - 4]}/close_affirmation.mp4`
        );

        const hasUserSong = response.status === 200;

        setLearnComponent(
          <Learn
            songVideo={video}
            destiny="/activelistening"
            song={hasUserSong ? userSongUrl : song}
            back="/activelistening"
            background={images.background}
            setDisplayLearn={setDisplayLearn}
            step_completed="MODULE15_SET_stepCompleted"
            module={module15}
            instrumental={hasUserSong ? getVideo('appfiles/active_listening/active_listening_final.m4a', login) : undefined}
            influencerVideo={influencerVideo}
          />
        );
      } catch (error) {
        console.error('Error loading learn content:', error);
      }
    }

    setDisplayLearn(true);
  };

  const openPractice = () => {
    const voice1 = getVideo('appfiles/close/close_practice_watch_conversation.mp3', login);
    const voice2 = getVideo('appfiles/close/close_awesome_job.mp3', login);
    const isEarlyDay = module15.currentday <= 3;

    if (isEarlyDay) {
      // Days 1-3: Standard practice with animal
      setPracticeComponent(
        <Practice
          setDisplayPractice={setDisplayPractice}
          background={images.background}
          stepComplete="MODULE15_SET_stepCompleted"
          module={module15}
          name={`active_listening_practice_video_${module15.currentday}.mp4`}
          conversationGIF={watchConversation}
          conversationAudio={voice1}
          endAudio={voice2}
          endGIF={birdAwesomeJobGIF}
          specialMessage={specialMessage}
          animal={images.bird}
        />
      );
    } else {
      // Days 4+: Practice with intro animation
      setAnimalVoice(getVideo('appfiles/active_listening/active_listening_practice_intro.mp3', login));
      setAnimalState(practiceIntro);
      setShowAnimal(true);
      setDisplayVoice(true);

      setPracticeComponent(
        <Practice
          setDisplayPractice={setDisplayPractice}
          background={images.background}
          stepComplete="MODULE15_SET_stepCompleted"
          module={module15}
          name={`active_listening_practice_video_${module15.currentday + 1}.mp4`}
          conversationGIF={watchConversation}
          conversationAudio={voice1}
          endAudio={voice2}
          endGIF={birdAwesomeJobGIF}
        />
      );
    }

    setDisplayPractice(true);
  };

  const openFTB = () => {
    setAnimalVoice(getVideo('appfiles/close/close_ftb_intro.mp3', login));
    setDisplayVoice(true);
    setAnimalState(ftbIntro);
    setShowAnimal(true);
    setDisplayFTB(true);
  };

  // Celebrate video configuration by day
  const celebrateVideosByDay = {
    1: 'appfiles/active_listening/celebrate_day_1.mp4',
    2: 'appfiles/active_listening/celebrate_day_2.mp4',
    3: 'appfiles/active_listening/celebrate_day_3.mp4',
  };

  const openCelebrate = async () => {
    const celebrateVideo = celebrateVideosByDay[module15.currentday];

    if (celebrateVideo) {
      // Days 1-3: Simple video celebration
      setCelebrateComponent(
        <Celebrate
          module={module15}
          celebrateVideo={getVideo(celebrateVideo, login)}
          background={images.background}
          setDisplayCelebrate={setDisplayCelebrate}
          moduleNum={MODULE_NAME}
        />
      );
    } else {
      // Days 4+: Interactive celebration with recording
      setAnimalVoice(getVideo('appfiles/close/close_celebrate_intro.mp3', login));
      setDisplayVoice(true);
      setAnimalState(celebrateIntro);
      setShowAnimal(true);

      const voice1 = getVideo('appfiles/close/close_celebrate_record_believe.mp3', login);
      const voice2 = getVideo('appfiles/close/close_celebrate_say_believe.mp3', login);
      const voice3 = getVideo('appfiles/close/close_awesome_job.mp3', login);
      const animalGIF = getVideo('appfiles/active_listening/joyful jingle bird.gif', login);
      const animalVoiceClip = getVideo('appfiles/active_listening/active_listening_joyful_jingle_intro.mp3', login);

      try {
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module15.currentday - 4]}/close_affirmation.mp4`
        );

        setCelebrateComponent(
          <Celebrate
            world3={true}
            module={module15}
            background={images.background}
            voice1={voice1}
            voice2={voice2}
            voice3={voice3}
            animalGIF={animalGIF}
            animalVoice={animalVoiceClip}
            text='Record yourself in an excited voice and say, "I believe in me!"'
            name={`active_listening_celebration_surprise_${module15.currentday + 1}.mp4`}
            influencerVideo={influencerVideo}
            animation={birdAwesomeJobGIF}
            animal={images.bird}
            setDisplayCelebrate={setDisplayCelebrate}
            moduleNum={MODULE_NAME}
            bigAnimal={true}
          />
        );
      } catch (error) {
        console.error('Error loading celebrate content:', error);
      }
    }

    setDisplayCelebrate(true);
  };

  const displaySinging = () => {
    const singingIntroVideo = getVideo('appfiles/active_listening/singing_time.mp4', login);
    const noteVideo = getVideo('appfiles/active_listening/active_listening_student_notes.mp4', login);
    const singingMusic = getVideo('appfiles/active_listening/active_listening_demo.m4a', login);
    const instrumental = getVideo('appfiles/active_listening/active_listening_instrumental.m4a', login);

    setSingTimeComponent(
      <SingingTime
        introVideo={singingIntroVideo}
        demoMusic={singingMusic}
        singingVideo={noteVideo}
        background={images.background}
        singingMusic={singingMusic}
        setDisplaySingingTime={setDisplaySingingTime}
        module={module15}
        moduleNum={MODULE_NAME}
        name="active_listening"
        instrumental={instrumental}
        instrumentalURL="appfiles/active_listening/active_listening_final.m4a"
      />
    );

    setDisplaySingingTime(true);
  };

  // ------------------------------------------------------------------------
  // Surprise Video
  // ------------------------------------------------------------------------

  const getSurpriseSlides = async () => {
    const videoPaths = [
      `influencers/${login.influencerid}/close_affirmation.mp4`,
      `userfiles/${module15.StudentID}/active_listening_celebration_surprise_5.mp4`,
      `influencers/${login.influencerid1}/close_affirmation.mp4`,
      `userfiles/${module15.StudentID}/active_listening_celebration_surprise_7.mp4`,
      `influencers/${login.influencerid2}/close_video_message.mp4`,
      `influencers/${login.influencerid3}/close_affirmation.mp4`,
      `userfiles/${module15.StudentID}/active_listening_celebration_surprise_9.mp4`,
      `influencers/${login.influencerid4}/close_affirmation.mp4`,
    ];

    const slides = await Promise.all(
      videoPaths.map(async (path) => {
        const video = await GetUserVideo(path, login);
        return { video };
      })
    );

    return slides;
  };

  const playSurpriseVideo = async () => {
    const slides = await getSurpriseSlides();

    setSurpriseVideo(
      <Surprise
        background={images.background}
        music={getVideo('appfiles/active_listening/active_listening_instrumental.m4a', login)}
        animal={images.bird}
        back="/"
        slides={slides}
        setDisplaySurprise={setDisplaySurprise}
      />
    );

    setDisplaySurprise(true);
    setKeyIcon(true);
    setPresentIcon(false);
  };

  // ------------------------------------------------------------------------
  // Animation Functions
  // ------------------------------------------------------------------------

  const coinAnimation = () => {
    setCoinAnimationState('coinAnimation');
    setCoinAudio(true);
  };

  const eggAnimation = () => {
    setKeyState('fade-out');
    eggCrackAudio.play();
    setEggHalf(true);

    sleep(2000).then(() => {
      setEggHalf(false);
      setEggState(images.eggBottom);
      setEggAnimationState('eggAnimate');
      setEggAnimate(true);

      sleep(1500).then(() => {
        setRewardsFound(true);
        setDisplayReward(true);
        setKeyIcon(false);
      });
    });
  };

  const closeDay9Video = () => {
    setDisplayIntroVideo(true);
    setDisplayBody(false);
    setIntroVideo(getVideo(AFTER_CELEBRATE_VIDEO_DAY9, login));
  };

  // ------------------------------------------------------------------------
  // Drag and Drop Handler
  // ------------------------------------------------------------------------

  const handleDragStop = (reward, data) => {
    const position = {
      x: data.x / clientWidth,
      y: data.y / clientHeight,
    };

    const isPrize = reward === 1 || reward === 2;
    const actionType = isPrize
      ? `MODULE15_SET_PRIZE${reward}_POSITION`
      : `MODULE15_SET_REWARD${reward}_POSITION`;

    dispatch({ type: actionType, payload: position });
  };

  // ------------------------------------------------------------------------
  // iOS Fallback Effects
  // ------------------------------------------------------------------------

  // iOS voice audio fallback
  useEffect(() => {
    if (!displayVoice || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setDisplayVoice(false);
      setAnimalState('');
      setShowAnimal(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [displayVoice]);

  // iOS animal close audio fallback
  useEffect(() => {
    if (!playAnimalClose || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setPlayAnimalClose(false);

      if (module15.currentday === SPECIAL_DAYS.DAY_8) {
        closeDay9Video();
      } else {
        navigate(NAVIGATION_PATH);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [playAnimalClose, module15.currentday, navigate]);

  // iOS coin audio fallback
  useEffect(() => {
    if (!coinAudio || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      dispatch({ type: 'MODULE15_SET_COIN', payload: module15.coins + 1 });
    }, 5000);

    return () => clearTimeout(timer);
  }, [coinAudio, dispatch, module15.coins]);

  // ------------------------------------------------------------------------
  // Audio Event Handlers
  // ------------------------------------------------------------------------

  const handleAnimalCloseAudioEnd = () => {
    setPlayAnimalClose(false);

    if (module15.currentday === SPECIAL_DAYS.DAY_8) {
      closeDay9Video();
    } else {
      navigate(NAVIGATION_PATH);
    }
  };

  const handleCoinAudioEnd = () => {
    dispatch({ type: 'MODULE15_SET_COIN', payload: module15.coins + 1 });
    setCoinAudio(false);
    setCoinAnimationState('bounceIcons');
    setCoinIcon(false);
    playButtonAudio();
  };

  const handleVoiceAudioEnd = () => {
    setDisplayVoice(false);
    setAnimalState('');
    setShowAnimal(false);
  };

  const handleLogout = () => {
    navigate('/');
    userHasAuthenticated(false);
    localStorage.setItem('token', 'false');
  };

  const handlePrizeClick = () => {
    setPrize(false);
    sleep(1400).then(() => {
      sleep(1000).then(() => {
        setDisplayPrize(true);
      });
    });
  };

  const handlePrizeButtonClick = () => {
    setPrize(false);
    setDisplayPrize(true);
  };

  // ------------------------------------------------------------------------
  // Render Helpers
  // ------------------------------------------------------------------------

  const shouldShowMainWorld =
    (displayBody &&
      !displayLearn &&
      !displayPractice &&
      !displaySingingTime &&
      !displayCelebrate &&
      !displaySurprise &&
      !displayFTB &&
      !displayDay4Practice) ||
    displayVoice;

  const isSpecialDay = module15.currentday === SPECIAL_DAYS.DAY_4 || module15.currentday === SPECIAL_DAYS.DAY_8;

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <>
      {/* Audio Elements */}
      {playAnimalClose && (
        <audio autoPlay onEnded={handleAnimalCloseAudioEnd}>
          <source src={animalCloseAudio} type="audio/mp3" />
        </audio>
      )}

      {coinAudio && (
        <audio autoPlay onEnded={handleCoinAudioEnd}>
          <source src={generateImageUrl('media/Module5Videos/earn_token.mp3')} type="audio/mp3" />
        </audio>
      )}

      {checkSound && (
        <audio autoPlay onEnded={() => setCheckSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_accept_item.mp3')} type="audio/mp3" />
        </audio>
      )}

      {rewardsFound && (
        <audio autoPlay>
          <source src={generateImageUrl('media/Module2Videos/daily_rewards_present.mp3')} />
        </audio>
      )}

      {touchButton && (
        <audio autoPlay onEnded={() => setTouchButton(false)}>
          <source src={generateImageUrl('media/Module1Videos/touch_button.mp3')} type="audio/mp3" />
        </audio>
      )}

      {buttonSound && (
        <audio autoPlay onEnded={() => setButtonSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_bouncing.mp3')} type="audio/mp3" />
        </audio>
      )}

      {displayVoice && (
        <audio autoPlay onEnded={handleVoiceAudioEnd}>
          <source src={animalVoice} />
        </audio>
      )}

      {/* Activity Screens */}
      {displayIntroVideo && renderIntroduction()}
      {!displayVoice && displayLearn && learnComponent}
      {!displayVoice && displayPractice && practiceComponent}

      {!displayVoice && displayFTB && (
        <FollowTheBeatModule10
          unit3={true}
          setDisplayFTB={setDisplayFTB}
          awesomeJob={amazingJob}
          awesomeJobGIF={birdAwesomeJobGIF}
          instruction={ftbInstruction}
          instructionGIF={ftbInstructionGIF}
          song={ftbMusic}
          count={ftbCount}
          tutorial1={ftbTutorial1}
          tutorial2={ftbTutorial2}
          walkthrough={ftbWalkthrough}
          stepComplete="MODULE15_SET_stepCompleted"
          module={module15}
          score="ftb_active_listening"
        />
      )}

      {!displayVoice && displayCelebrate && celebrateComponent}
      {displaySingingTime && singTimeComponent}
      {displaySurprise && surpriseVideo}

      {/* Main World Content */}
      {shouldShowMainWorld && (
        <div
          className="activeListeningWorldBody"
          style={{ backgroundImage: `url(${images.sky})` }}
        >
          <img
            className="activeListeningMountain"
            src={images.mountain}
            alt="mountain"
          />

          {/* Navigation Bar */}
          <div className="navigation">
            <img
              className="logoutBtn"
              src={images.logoutBtn}
              alt="Logout Button"
              onClick={togglePopup}
            />
            <img
              className="JourneyBlueImage"
              src={images.journeyMap}
              alt="Navigation blue world"
              onClick={() => navigate(NAVIGATION_PATH)}
            />
          </div>

          {/* Logout Popup */}
          {isPopupOpen && (
            <PopUp
              content={
                <>
                  <b id="title">
                    Are you sure you want to leave?
                    <br />
                  </b>
                  <button
                    id="logoutButton1"
                    className="logoutButton"
                    onClick={handleLogout}
                  >
                    YES
                  </button>
                  <button
                    id="logoutButton2"
                    className="logoutButton"
                    onClick={togglePopup}
                  >
                    NO
                  </button>
                </>
              }
              handleClose={togglePopup}
            />
          )}

          {/* Prize Modal */}
          {prize && (
            <audio autoPlay>
              <source src={presentSound} />
            </audio>
          )}

          {prize && (
            <img
              className="prize"
              src={generateImageUrl('images/Images/prize.png')}
              alt="Prize"
              onClick={handlePrizeClick}
            />
          )}

          {prize && (
            <button className="prize-button" onClick={handlePrizeButtonClick}>
              <h5>Click Here</h5>
            </button>
          )}

          {displayPrize && renderPrizeSelection()}

          {/* Bird Container */}
          <div className="Bird_Container" style={{ zIndex: 5 }}>
            {showAnimal && (
              <img className="birdCharacterGif" src={animalState} alt="bird animation" />
            )}
            <img className="birdCharacter" src={images.bird} alt="bird Icon" />
            <img className="Bird_Egg" src={eggState} alt="Egg Icon" />
            <img className="Bird_Nest" src={images.nest} alt="Nest Icon" />
            {eggHalf && (
              <img
                className="Bird_Egg"
                src={images.eggHalfCracked}
                id="eggFade"
                alt="Cracking Egg"
              />
            )}
            {eggAnimate && (
              <img
                className="Bird_Egg"
                src={images.eggTop}
                id={eggAnimationState}
                alt="Egg Top"
              />
            )}
          </div>

          {/* Score Bar */}
          <div className="scoreBar">
            <img className="scoreKey" src={images.coinBar} alt="Coin Counter" />
            {module15.coins < 10 && <h5 className="scoretext1">{module15.coins}</h5>}
            {module15.coins >= 10 && module15.coins < 100 && (
              <h5 className="scoretext10">{module15.coins}</h5>
            )}
            {module15.coins >= 100 && <h5 className="scoretext100">{module15.coins}</h5>}
            <p className="scoretext">100</p>
          </div>

          {/* Progress Bar */}
          <div className="progressBar3">
            <img
              className="progressKey3"
              src={isSpecialDay ? progressBar5Keys[module15.keys] : progressBar4Keys[module15.keys]}
              alt="Progress Bar"
            />
            <h3 className="progresstext3">
              {module15.keys}/{isSpecialDay ? 5 : 4}
            </h3>
          </div>

          {/* Action Buttons - Current Step */}
          {!showAnimal && !module15.reviewIcon && !coinIcon && !displayPrize && !dropZone && (
            <div className="actionBtnDiv">
              {/* Day 4 Singing Time Start */}
              {module15.currentday === SPECIAL_DAYS.DAY_4 &&
                module15.stepCompleted === STEPS.NOT_STARTED && (
                  <img
                    id="bounceIcons"
                    className="actionBtn2"
                    src={images.singingTimeBtn}
                    alt="singing time button"
                    onClick={() => {
                      setTouchButton(true);
                      sleep(500).then(() => {
                        displaySinging();
                        setCheckStep(STEPS.SINGING_TIME);
                      });
                    }}
                  />
                )}

              {/* Standard Learn Start */}
              {module15.currentday !== SPECIAL_DAYS.DAY_4 &&
                module15.stepCompleted === STEPS.NOT_STARTED && (
                  <img
                    id="bounceIcons"
                    className="actionBtn2"
                    src={images.learnBtn}
                    alt="learn button"
                    onClick={() => {
                      setTouchButton(true);
                      sleep(500).then(() => {
                        openLearn();
                        setCheckStep(STEPS.LEARN);
                      });
                    }}
                  />
                )}

              {/* Practice Button */}
              {module15.stepCompleted === STEPS.LEARN && (
                <img
                  className="actionBtn2"
                  src={images.practiceBtn}
                  id="bounceIcons"
                  alt="practice button"
                  onClick={() => {
                    touchSound.play();
                    sleep(500).then(() => {
                      openPractice();
                      setCheckStep(STEPS.PRACTICE);
                    });
                  }}
                />
              )}

              {/* Follow The Beat Button */}
              {module15.stepCompleted === STEPS.PRACTICE && (
                <img
                  className="actionBtn2"
                  src={images.ftbBtn}
                  id="bounceIcons"
                  alt="follow the beat button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openFTB();
                      setCheckStep(STEPS.FOLLOW_THE_BEAT);
                    });
                  }}
                />
              )}

              {/* Celebrate Button */}
              {module15.stepCompleted === STEPS.FOLLOW_THE_BEAT && (
                <img
                  className="actionBtn2"
                  src={images.celebrateBtn}
                  id="bounceIcons"
                  alt="celebrate button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openCelebrate();
                      setCheckStep(STEPS.CELEBRATE);
                    });
                  }}
                />
              )}

              {/* Learn after Singing Time (Day 4) */}
              {module15.stepCompleted === STEPS.SINGING_TIME && (
                <img
                  className="actionBtn2"
                  src={images.learnBtn}
                  id="bounceIcons"
                  alt="learn button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      openLearn();
                      setCheckStep(STEPS.LEARN);
                    });
                  }}
                />
              )}
            </div>
          )}

          {/* Coin Collection */}
          {!displayVoice && coinIcon && !displayPrize && (
            <div className="actionBtnDiv">
              {!module15.reviewIcon && (
                <img
                  id={coinAnimationState}
                  src={images.coinPick}
                  alt="reward coin"
                  style={{
                    position: 'absolute',
                    top: '50vh',
                    left: '50vw',
                    width: '100px',
                    height: '100px',
                    marginLeft: '-50px',
                    marginTop: '-50px',
                    zIndex: 10,
                  }}
                  onClick={coinAnimation}
                />
              )}
            </div>
          )}

          {/* Review Mode Buttons */}
          {!displayPrize && !showAnimal && !displayReward && (
            <div className="actionBtnDiv">
              {module15.reviewIcon && (
                <img
                  className={showReward ? 'actionBtn3' : 'actionBtn2'}
                  src={images.reviewBtn}
                  alt="review button"
                  onClick={() => setReviewIconVisible(!reviewIconVisible)}
                />
              )}
              {showReward && (
                <img
                  src={images.rewardBtn}
                  className="actionBtn4"
                  alt="reward button"
                  onClick={() => {
                    setTouchButton(true);
                    sleep(500).then(() => {
                      eggAnimation();
                    });
                  }}
                />
              )}
              {module15.reviewIcon && reviewIconVisible && renderReviewButtons()}
            </div>
          )}

          {/* Key/Worm Reward */}
          {!coinIcon && keyIcon && !displayPrize && (
            <img
              className="rewardsIcon"
              id={keyState}
              style={{
                width: '150px',
                height: '40px',
                marginLeft: '-75px',
                zIndex: 10,
              }}
              src={images.keyReward}
              alt="reward key"
              onClick={eggAnimation}
            />
          )}

          {displayReward && renderRewardSelection()}

          {/* Present/Gift Icon (Day 8 surprise) */}
          {!displayPrize && !coinIcon && presentIcon && (
            <>
              <img
                className="present"
                style={{
                  width: '84px',
                  height: '100px',
                  marginLeft: '-42px',
                  zIndex: 10,
                }}
                id="bounceIcons"
                src={images.present}
                alt="gift"
                onClick={playSurpriseVideo}
              />
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
            </>
          )}

          {/* Reward Area with Draggable Items */}
          <div className="rewardArea">
            <img className="Tree" src={images.tree} alt="Tree" />

            {/* Tree House Rewards */}
            {module15.rewards.TreeHouseRed.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.TreeHouseRed.x * clientWidth,
                  y: module15.rewards.TreeHouseRed.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(11, data)}
              >
                <div
                  className="moveBigReward1"
                  style={{ backgroundImage: `url(${images.rewards.TreeHouseRed})` }}
                />
              </Draggable>
            )}

            {module15.rewards.TreeHouseBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.TreeHouseBlue.x * clientWidth,
                  y: module15.rewards.TreeHouseBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(12, data)}
              >
                <div
                  className="moveBigReward2"
                  style={{ backgroundImage: `url(${images.rewards.TreeHouseBlue})` }}
                />
              </Draggable>
            )}

            {/* Alien Rewards */}
            {module15.rewards.Alien1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Alien1.x * clientWidth,
                  y: module15.rewards.Alien1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(21, data)}
              >
                <div
                  className="moveAlien1"
                  style={{ backgroundImage: `url(${images.rewards.Alien1})` }}
                />
              </Draggable>
            )}

            {module15.rewards.Alien2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Alien2.x * clientWidth,
                  y: module15.rewards.Alien2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(22, data)}
              >
                <div
                  className="moveAlien2"
                  style={{ backgroundImage: `url(${images.rewards.Alien2})` }}
                />
              </Draggable>
            )}

            {/* Telescope Rewards */}
            {module15.rewards.TelescopeRed.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.TelescopeRed.x * clientWidth,
                  y: module15.rewards.TelescopeRed.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(31, data)}
              >
                <div
                  className="moveTelescope1"
                  style={{ backgroundImage: `url(${images.rewards.TelescopeRed})` }}
                />
              </Draggable>
            )}

            {module15.rewards.TelescopeBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.TelescopeBlue.x * clientWidth,
                  y: module15.rewards.TelescopeBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(32, data)}
              >
                <div
                  className="moveTelescope2"
                  style={{ backgroundImage: `url(${images.rewards.TelescopeBlue})` }}
                />
              </Draggable>
            )}

            {/* Comet Rewards */}
            {module15.rewards.CometBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.CometBlue.x * clientWidth,
                  y: module15.rewards.CometBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(41, data)}
              >
                <div
                  className="moveComet1"
                  style={{ backgroundImage: `url(${images.rewards.CometBlue})` }}
                />
              </Draggable>
            )}

            {module15.rewards.CometOrange.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.CometOrange.x * clientWidth,
                  y: module15.rewards.CometOrange.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(42, data)}
              >
                <div
                  className="moveComet2"
                  style={{ backgroundImage: `url(${images.rewards.CometOrange})` }}
                />
              </Draggable>
            )}

            {/* Deer Rewards */}
            {module15.rewards.Deer.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Deer.x * clientWidth,
                  y: module15.rewards.Deer.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(51, data)}
              >
                <div
                  className="moveDeer1"
                  style={{ backgroundImage: `url(${images.rewards.Deer})` }}
                />
              </Draggable>
            )}

            {module15.rewards.Doe.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Doe.x * clientWidth,
                  y: module15.rewards.Doe.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(52, data)}
              >
                <div
                  className="moveDeer2"
                  style={{ backgroundImage: `url(${images.rewards.Doe})` }}
                />
              </Draggable>
            )}

            {/* Planet Rewards */}
            {module15.rewards.Planet1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Planet1.x * clientWidth,
                  y: module15.rewards.Planet1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(61, data)}
              >
                <div
                  className="movePlanet1"
                  style={{ backgroundImage: `url(${images.rewards.Planet1})` }}
                />
              </Draggable>
            )}

            {module15.rewards.Planet2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Planet2.x * clientWidth,
                  y: module15.rewards.Planet2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(62, data)}
              >
                <div
                  className="movePlanet2"
                  style={{ backgroundImage: `url(${images.rewards.Planet2})` }}
                />
              </Draggable>
            )}

            {/* Swing Rewards */}
            {module15.rewards.Swing.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Swing.x * clientWidth,
                  y: module15.rewards.Swing.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(71, data)}
              >
                <div
                  className="moveSwing1"
                  style={{ backgroundImage: `url(${images.rewards.Swing})` }}
                />
              </Draggable>
            )}

            {module15.rewards.TireSwing.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.TireSwing.x * clientWidth,
                  y: module15.rewards.TireSwing.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(72, data)}
              >
                <div
                  className="moveSwing2"
                  style={{ backgroundImage: `url(${images.rewards.TireSwing})` }}
                />
              </Draggable>
            )}

            {/* Rocket Rewards */}
            {module15.rewards.Rocket1.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Rocket1.x * clientWidth,
                  y: module15.rewards.Rocket1.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(81, data)}
              >
                <div
                  className="moveRocket1"
                  style={{ backgroundImage: `url(${images.rewards.Rocket1})` }}
                />
              </Draggable>
            )}

            {module15.rewards.Rocket2.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Rocket2.x * clientWidth,
                  y: module15.rewards.Rocket2.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(82, data)}
              >
                <div
                  className="moveRocket2"
                  style={{ backgroundImage: `url(${images.rewards.Rocket2})` }}
                />
              </Draggable>
            )}

            {/* Big Prize Rewards */}
            {module15.rewards.BigNorthernLights.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.BigNorthernLights.x * clientWidth,
                  y: module15.rewards.BigNorthernLights.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(1, data)}
              >
                <div
                  className="movePrize15"
                  style={{ backgroundImage: `url(${images.rewards.NorthernLights})` }}
                />
              </Draggable>
            )}

            {module15.rewards.Galaxy.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module15.rewards.Galaxy.x * clientWidth,
                  y: module15.rewards.Galaxy.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(2, data)}
              >
                <div
                  className="movePrize25"
                  style={{ backgroundImage: `url(${images.rewards.Galaxy})` }}
                />
              </Draggable>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Module15World;
